import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment';
import { FiUsers, FiMail, FiTrendingUp, FiFileText, FiSearch, FiChevronDown, FiRefreshCw } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { RegisterListingHolder, RegisterListingResult, RegisterListingWebUser } from '../../../model/RegisterListingResult';
import api from '../../../api/api';
import { to } from '../../../util/util';
import ConfirmEx from '../../ConfirmEx';

// ShareRegistryProvider enum order (investi.Core/Model/ShareRegistryProvider.cs)
export const providers = ['None', 'Automic', 'Xcend', 'Computershare', 'MUFG', 'Advanced', 'Boardroom'];
export const registerAsAt = (asAt?: string | null) => asAt ? moment(asAt).calendar(null, { sameDay: '[today], D MMMM YYYY, h:mm a', lastDay: '[yesterday], D MMMM YYYY, h:mm a', lastWeek: 'dddd, D MMMM YYYY, h:mm a', sameElse: 'D MMMM YYYY, h:mm a' }) : null;
const states = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];
const sizes: Record<string, [number, number]> = { 'Any': [0, Infinity], 'Under 10,000': [0, 10000], '10,000 – 100,000': [10000, 100000], '100,000 – 1M': [100000, 1e6], 'Over 1M': [1e6, Infinity] };
export const pageSizes = [25, 50, 100];

export const pageList = (pages: number, current: number): (number | '…')[] => {
    if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
    const set = new Set([1, 2, 3, current - 1, current, current + 1, pages].filter(p => p >= 1 && p <= pages));
    const out: (number | '…')[] = [];
    Array.from(set).sort((a, b) => a - b).forEach((p, i, arr) => { if (i && p - arr[i - 1] > 1) out.push('…'); out.push(p); });
    return out;
};

// "12 SMITH ST, SYDNEY NSW 2000" → { location: "Sydney NSW", state: "NSW" }; overseas → last part
const parseAddress = (address?: string) => {
    const m = /([A-Z][A-Z' -]*?)\s+(NSW|VIC|QLD|WA|SA|TAS|ACT|NT)\s+\d{4}/i.exec(address || '');
    if (m) return { location: `${title(m[1])} ${m[2].toUpperCase()}`, state: m[2].toUpperCase() };
    const parts = (address || '').split(',').map(s => s.trim()).filter(Boolean);
    return { location: parts.length ? title(parts[parts.length - 1]) : '', state: parts.length ? 'Overseas' : '' };
};
const title = (s: string) => s.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

interface Entry {
    holderId?: number; name: string; email: string; holderNumber: string; holderType: string; holding: number;
    change: number; // change over result.balanceHistoricalDays in the primary security; ignored until balanceHistoricalDate is set
    location: string; state: string;
    members?: Entry[]; // holder group aggregate row
    webUser?: RegisterListingWebUser;
}

const AllHolders: React.FC = () => {
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const history = useHistory();
    const [result, setResult] = useState<RegisterListingResult | null>(null);
    const [error, setError] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const [query, setQuery] = useState('');
    const [type, setType] = useState('All');
    const [hasEmail, setHasEmail] = useState('Any');
    const [size, setSize] = useState('Any');
    const [state, setState] = useState('Any');
    const [pageSize, setPageSize] = useState(25);
    const [page, setPage] = useState(1);

    const load = async (fetch: () => Promise<RegisterListingResult>) => {
        const [err, data] = await to(fetch());
        if (err || !data.success) setError(err?.message || data?.message || 'Failed to fetch register listing');
        else { setError(''); setResult(data); }
    };
    useAsyncEffect(async () => {
        if (!stock) return;
        setResult(null);
        await load(() => api().getRegisterListing(stock.ticker, stock.exchange));
    }, [stock?.id]);

    const provider = providers[stock?.shareRegistryProvider ?? 0] ?? 'registry';

    const refresh = async () => {
        if (!stock || !await ConfirmEx(`Fetch the latest register from ${provider} now? This can take a minute.`)) return;
        setRefreshing(true);
        await load(() => api().refreshRegisterListing(stock.ticker, stock.exchange));
        setRefreshing(false);
    };

    // only the primary listed security — options etc. are visible on the holder details page
    const primaryIndex = Math.max((result?.securityCodes || []).indexOf(stock?.ticker || ''), 0);

    // only holders of the primary security: the listing also carries sold-out and options-only holders
    const holders = useMemo(() => (result?.holders || []).filter(h => (h.balances?.[primaryIndex] ?? 0) > 0), [result, primaryIndex]);

    // same shape as v1 RegisterListing: holders sharing a holderGroup collapse into one aggregate row,
    // non-holder web users sit at the bottom
    const { entries, total } = useMemo(() => {
        const toEntry = (h: RegisterListingHolder): Entry => ({
            holderId: h.holderId, name: h.name, email: h.email, holderNumber: h.holderNumber, holderType: h.holderType,
            holding: h.balances?.[primaryIndex] ?? 0, ...parseAddress(h.address),
            change: (h.balances?.[primaryIndex] ?? 0) - (h.balanceHistorical ?? 0),
        });
        const groups = new Map<string, Entry[]>();
        const singles: Entry[] = [];
        holders.forEach(h => {
            if (!h.holderGroup) { singles.push(toEntry(h)); return; }
            groups.set(h.holderGroup, [...(groups.get(h.holderGroup) || []), toEntry(h)]);
        });
        const grouped: Entry[] = Array.from(groups.entries()).map(([name, members]) => ({
            name, email: '', holderNumber: '', holderType: `Group · ${members.length} holders`, location: '', state: '',
            holding: members.reduce((s, m) => s + m.holding, 0),
            change: members.reduce((s, m) => s + m.change, 0),
            members: members.sort((a, b) => b.holding - a.holding),
        }));
        const ranked = [...singles, ...grouped].sort((a, b) => b.holding - a.holding);
        const webUsers: Entry[] = (result?.webUsers || []).map(w => ({
            name: w.name || w.email, email: w.email, holderNumber: '', holderType: 'Web user', holding: 0, change: 0, location: '', state: '', webUser: w,
        }));
        return { entries: [...ranked, ...webUsers], total: ranked.reduce((s, x) => s + x.holding, 0) };
    }, [holders, result]);

    const holderTypes = useMemo(() => Array.from(new Set(holders.map(h => h.holderType).filter(Boolean))).sort(), [holders]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        const text = (e: Entry) => [e.name, e.email, e.holderNumber].some(c => (c || '').toLowerCase().includes(q));
        const [lo, hi] = sizes[size];
        return entries.filter(e =>
            (!q || text(e) || (e.members || []).some(text)) &&
            (type === 'All' || e.holderType === type || (e.members || []).some(m => m.holderType === type)) &&
            (hasEmail === 'Any' || (hasEmail === 'Yes') === !!(e.email || (e.members || []).some(m => m.email))) &&
            (size === 'Any' || (e.holding >= lo && e.holding < hi)) &&
            (state === 'Any' || e.state === state || (e.members || []).some(m => m.state === state)));
    }, [entries, query, type, hasEmail, size, state]);

    // rank is fixed by holding on the unfiltered list so it survives filtering
    const rank = useMemo(() => new Map(entries.map((e, i) => [e, i + 1])), [entries]);
    const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const current = Math.min(page, pages);
    const rows = filtered.slice((current - 1) * pageSize, current * pageSize);
    const resetPage = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPage(1); };

    const holderCount = result?.holderCount ?? holders.length;
    const weekAgo = result?.holderCountWeekAgo;
    const delta = weekAgo ? holderCount - weekAgo : null;
    const withEmail = holders.filter(h => h.email).length;
    const top20 = total ? entries.filter(e => !e.webUser).slice(0, 20).reduce((s, e) => s + e.holding, 0) * 100 / total : 0;
    const asAt = registerAsAt(result?.asAt);


    const select = (label: string, value: string, options: string[], set: (v: string) => void) =>
        <label className="filter">
            <select value={value} onChange={e => set(e.target.value)}>{options.map(o => <option key={o} value={o}>{label}: {o}</option>)}</select>
            <FiChevronDown />
        </label>;

    // group aggregate rows have no holder of their own; members and web users open the holder page
    const open = (e: Entry) => e.webUser ? history.push(`/share-registry/holder/web?email=${encodeURIComponent(e.email)}`)
        : e.holderId ? history.push(`/share-registry/holder/${e.holderId}`) : undefined;
    const Row = ({ e, member }: { e: Entry; member?: boolean }) => <div className={`row${member ? ' member' : ''}${e.webUser ? ' web-user' : ''}${e.members ? '' : ' clickable'}`} onClick={() => open(e)}>
        <span className="rank">{member ? '' : rank.get(e)}</span>
        <div className="holder">
            <div className="name">{member && <span className="arrow">↳ </span>}{e.name}</div>
            <div className="sub">{[e.holderType, e.holderNumber, e.location].filter(Boolean).join(' · ')}</div>
        </div>
        <span className={`email${e.email ? '' : ' none'}`}>{e.email ? <><FiMail /> {e.email}</> : e.members ? '' : 'No email on file'}</span>
        <span className="num">{e.webUser || member ? '' : e.holding.toLocaleString()}</span>
        <span className="num">{e.webUser || member ? '' : `${(total ? e.holding * 100 / total : 0).toFixed(2)}%`}</span>
        <span>{!e.webUser && !member && (result?.balanceHistoricalDate
            ? <span className={`pill${e.change > 0 ? ' ok' : e.change < 0 ? ' bad' : ''}`}>{e.change > 0 ? '+' : ''}{e.change.toLocaleString()}</span>
            : <span className="pill">—</span>)}</span>
        <span>{!e.webUser && !member && <span className="pill">TBA</span>}</span>
    </div>;

    return <div className="all-holders">
        <div className="page-header">
            <div>
                <h1>Share Registry</h1>
                <p>{[stock?.companyName, provider !== 'None' && provider, asAt && `Register as at ${asAt}`].filter(Boolean).join(' · ')}</p>
            </div>
            <button type="button" className="btn-primary" onClick={refresh} disabled={refreshing || !result}>
                <FiRefreshCw className={refreshing ? 'spin' : ''} /> {refreshing ? 'Refreshing…' : `Refresh from ${provider}`}
            </button>
        </div>

        {error && <p className="error">{error}</p>}
        {!error && !result && <p className="loading">Loading register listing… (first load can take a minute while reports generate)</p>}

        {result && <>
            <div className="stats">
                <div className="stat">
                    <div className="h"><span className="icon-tile"><FiUsers /></span> Total holders
                        {delta !== null && <span className={`pill ${delta >= 0 ? 'ok' : 'bad'}`}>{delta >= 0 ? '+' : ''}{weekAgo ? (delta * 100 / weekAgo).toFixed(1) : 0}%</span>}</div>
                    <div className="v">{holderCount.toLocaleString()}</div>
                    <div className="d">{delta === null ? 'No count from last week yet' : `${delta >= 0 ? '+' : ''}${delta.toLocaleString()} since last week`}</div>
                </div>
                <div className="stat">
                    <div className="h"><span className="icon-tile"><FiMail /></span> Holders with an email</div>
                    <div className="v">{withEmail.toLocaleString()}</div>
                    <div className="d">{holderCount ? Math.round(withEmail * 100 / holderCount) : 0}% you can contact directly</div>
                </div>
                <div className="stat">
                    <div className="h"><span className="icon-tile"><FiTrendingUp /></span> Top 20 Share holders</div>
                    <div className="v">{top20.toFixed(1)}%</div>
                    <div className="d">of issued capital</div>
                </div>
                <div className="stat">
                    <div className="h"><span className="icon-tile"><FiFileText /></span> Issued capital</div>
                    <div className="v">{total.toLocaleString()}</div>
                    <div className="d">fully paid ordinary shares</div>
                </div>
            </div>

            <div className="card">
                <div className="filters">
                    <label className="search"><FiSearch /><input placeholder="Search by name, holder number or email…" value={query} onChange={e => resetPage(setQuery)(e.target.value)} /></label>
                    {select('Holder Type', type, ['All', ...holderTypes], resetPage(setType))}
                    {select('Has Email', hasEmail, ['Any', 'Yes', 'No'], resetPage(setHasEmail))}
                    {select('Holding Size', size, Object.keys(sizes), resetPage(setSize))}
                    {select('State', state, ['Any', ...states, 'Overseas'], resetPage(setState))}
                    <span className="count">Showing {filtered.length.toLocaleString()} of {entries.length.toLocaleString()} holders</span>
                </div>

                <div className="table">
                    <div className="row head">
                        <span>#</span><span>Holder</span><span>Email</span><span className="num">Holding</span><span className="num">% Issued</span><span>{result?.balanceHistoricalDays ?? 30}-day change</span><span>Segment</span>
                    </div>
                    {rows.map(e => <React.Fragment key={e.webUser ? `w:${e.email}` : e.holderNumber || e.name}>
                        <Row e={e} />
                        {e.members?.map(m => <Row key={m.holderNumber} e={m} member />)}
                    </React.Fragment>)}
                    {rows.length === 0 && <p className="empty">No holders match</p>}
                </div>

                <div className="footer">
                    <span className="count">Showing {filtered.length ? (current - 1) * pageSize + 1 : 0}–{Math.min(current * pageSize, filtered.length)} of {filtered.length.toLocaleString()} holders</span>
                    <label className="filter rows">
                        <select value={pageSize} onChange={e => resetPage(setPageSize)(Number(e.target.value))}>{pageSizes.map(n => <option key={n} value={n}>Rows per page: {n}</option>)}</select>
                        <FiChevronDown />
                    </label>
                    <div className="pagination">
                        <button type="button" disabled={current === 1} onClick={() => setPage(current - 1)}>‹</button>
                        {pageList(pages, current).map((p, i) => p === '…' ? <span key={`e${i}`}>…</span>
                            : <button type="button" key={p} className={p === current ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>)}
                        <button type="button" disabled={current === pages} onClick={() => setPage(current + 1)}>›</button>
                    </div>
                </div>
            </div>
        </>}
    </div>;
};

export default AllHolders;
